import { Component, EventEmitter, HostListener, inject, Input, OnInit, Output, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { CustomerNote } from '../../../../models/customer-note';
import { Params } from '../../../../services/utilis/add-params-service';
import { CustomerNotesService } from '../../../../services/customers/customer-notes-service';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs';
import { PaginatorState, PaginatorModule } from 'primeng/paginator';
import { UpdateCustomerNoteForm } from '../../../../@shared/forms/customers/update-customer-note-form/update-customer-note-form';
import { DeleteCustomerNoteForm } from '../../../../@shared/forms/customers/delete-customer-note-form/delete-customer-note-form';
import { GenericPaginated } from '../../../../models/generic-paginated';
import { NotePhotos } from '../../../../@shared/components/note-photos/note-photos';

@Component({
  selector: 'app-customer-details-notes-component',
  imports: [DatePipe, PaginatorModule, UpdateCustomerNoteForm, DeleteCustomerNoteForm, NotePhotos],
  templateUrl: './customer-details-notes-component.html',
  styleUrl: './customer-details-notes-component.scss'
})
export class CustomerDetailsNotesComponent implements OnInit {

  @Input() customerID: string | undefined
  @Output() addNoteClicked = new EventEmitter<void>();

  params = ['page', 'size', 'sortBy', 'sortDirection', 'createdFrom', 'createdTo']

  modifiedParams: Params = {
    page: 'page', size: 'size', sortBy: 'sortBy',
    sortDirection: 'sortDirection', createdFrom: 'createdFrom', createdTo: 'createdTo',
  }

  customerNotesList = signal<CustomerNote[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;

  activeMenuNoteId: number | null = null;
  selectedCustomerNote: CustomerNote
  updateCustomerNoteDialog = false
  deleteCustomerNoteDialog = false

  private lastParams = new BehaviorSubject<Record<string, unknown> | null>(null);
  private customerNotesService = inject(CustomerNotesService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)

  ngOnInit() {
    this.getCustomerNotesList()
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : parts[0][0].toUpperCase();
  }

  toggleMenu(event: Event, note: CustomerNote) {
    event.stopPropagation();
    this.activeMenuNoteId = this.activeMenuNoteId === note.id ? null : note.id;
  }

  @HostListener('document:click')
  closeMenu() {
    this.activeMenuNoteId = null;
  }

  getCustomerNotesList = () => {
    this.activatedRoute.queryParams.pipe(
      debounceTime(0),
      filter(params => params['tab'] === '2'),
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      filter((queryParams) => {
        if (JSON.stringify(this.lastParams.value) === JSON.stringify(queryParams)) return false;
        this.lastParams.next(queryParams);
        return true;
      }),
      tap((queryParams) => {
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),
      switchMap((queryParams) => this.fetchNotes(queryParams))
    ).subscribe(this.setNotes);
  }

  /** Re-fetch the current page, e.g. after a note was added, updated or deleted */
  refreshList = () => {
    this.fetchNotes((this.lastParams.value ?? {}) as Params).subscribe(this.setNotes);
  }

  private fetchNotes = (queryParams: Params) => {
    const params = {
      ...queryParams,
      sortBy: queryParams['sortBy'] ?? 'updatedAt',
      sortDirection: queryParams['sortDirection'] ?? 'DESC',
    };
    return this.customerNotesService.getCustomerNotesList(this.customerID!, params);
  }

  private setNotes = (res: GenericPaginated<CustomerNote>) => {
    this.customerNotesList.set(res.data.content);
    this.totalRecords.set(res.data.totalElements)
    this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
  }

  onPageChange = (event: PaginatorState) => {
    this.router.navigate([], {
      queryParams: { page: event.page, size: event.rows },
      queryParamsHandling: 'merge'
    })
  }

  resetAll = () => {
    this.lastParams.next(null);
  }

  openUpdateCustomerNoteForm = (selectedRow: CustomerNote) => {
    this.activeMenuNoteId = null;
    this.selectedCustomerNote = selectedRow
    this.updateCustomerNoteDialog = true
  }

  onCustomerNoteUpdated = () => {
    this.refreshList()
    this.updateCustomerNoteDialog = false
  }

  openDeleteCustomerNoteForm = (selectedRow: CustomerNote) => {
    this.activeMenuNoteId = null;
    this.selectedCustomerNote = selectedRow
    this.deleteCustomerNoteDialog = true
  }

  onCustomerNoteDeleted = () => {
    this.refreshList()
    this.deleteCustomerNoteDialog = false
  }
}
